import { SearchParamsOption } from "ky";
import createApiClient from "./createApiClient";
import { MAIN_API_ROOT } from "../../app_config";
import {
  deleteTokens,
  getAccessToken,
  getRefreshToken,
  saveAccessToken,
  saveRefreshToken,
  saveRefreshTokenId,
} from "../../helpers/tokenManager";
import publicCustomerUserClient from "../public/publicCustomerUserClient";
import { jwtDecode, JwtPayload } from "jwt-decode";

const TOKEN_REFRESH_TRESHOLD = 0.2; // 20%
let isRefreshing = false;

export const refreshToken = async () => {
  if (isRefreshing) return;
  isRefreshing = true;

  try {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      const result = await publicCustomerUserClient.refreshTokens(refreshToken);

      if (result.success) {
        saveRefreshToken(result.refreshToken);
        saveRefreshTokenId(result.refreshTokenId);
        saveAccessToken(result.accessToken);
        return;
      }
    }
  } catch {
    deleteTokens();
    window.location.replace("/");
  } finally {
    isRefreshing = false;
  }
};

const isTokenNearToExpiry = (token: string) => {
  const decodedJwt = jwtDecode<JwtPayload>(token);

  if (decodedJwt.exp && decodedJwt.iat) {
    const lifetimeTreshold =
      (decodedJwt.exp - decodedJwt.iat) * TOKEN_REFRESH_TRESHOLD;

    return (decodedJwt.exp - lifetimeTreshold) * 1000 < Date.now();
  }
};

export const privateApiClient = createApiClient(MAIN_API_ROOT).extend({
  hooks: {
    beforeRequest: [
      async (request) => {
        let token = getAccessToken();

        if (isTokenNearToExpiry(token)) {
          await refreshToken();
          token = getAccessToken();
        }

        request.headers.set("Authorization", `Bearer ${token}`);
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        // You could do something with the response, for example, logging.
        if (response.status === 401) {
          await refreshToken();
        }
      },
    ],
  },
});

export const privateGet = async <T>(
  endpoint: string,
  searchParams?: SearchParamsOption,
) => await privateApiClient.get(endpoint, { searchParams }).json<T>();

export const privatePost = async <T>(endpoint: string, values: unknown) =>
  await privateApiClient.post(endpoint, { json: values }).json<T>();

export const privatePut = async <T>(endpoint: string, values: unknown) =>
  await privateApiClient.put(endpoint, { json: values }).json<T>();

export const privateDelete = async <T>(endpoint: string) =>
  await privateApiClient.delete(endpoint).json<T>();
